import { Component, HostBinding, OnDestroy } from '@angular/core';
import { ScreenService, AppInfoService } from './shared/services';

import enDevExpressMessages from '../assets/i18n/en-devexpress.json';
import enMessages from '../assets/i18n/en.json';
import deDevExpressMessages from '../assets/i18n/de-devexpress.json';
import deMessages from '../assets/i18n/de.json';
import itDevExpressMessages from '../assets/i18n/it-devexpress.json';
import itMessages from '../assets/i18n/it.json';
import csDevExpressMessages from '../assets/i18n/cs-devexpress.json';
import csMessages from '../assets/i18n/cs.json';
import esDevExpressMessages from '../assets/i18n/es-devexpress.json';
import esMessages from '../assets/i18n/es.json';
import frDevExpressMessages from '../assets/i18n/fr-devexpress.json';
import frMessages from '../assets/i18n/fr.json';
import trDevExpressMessages from '../assets/i18n/tr-devexpress.json';
import trMessages from '../assets/i18n/tr.json';

import { TranslateService } from '@ngx-translate/core';
import { loadMessages, locale } from 'devextreme/localization';
import { Observable, Subject, take, takeUntil } from 'rxjs';
import { AuthService, AuthUserService, TranslateWithFallbackPipe } from 'acl-cloud-lib';
import { LoadingService } from './shared/services/loading.service';
import { MachineOwner, User, VersionService } from '../api/generated';
import { RemoteDataWrapperService } from './shared/services/remote-data/remote-data-wrapper.service';
import { DashboardDataService } from './pages/dashboard-page/data/dashboard-data.service';
import { SwUpdate } from '@angular/service-worker';
import { IndexedDBService } from './shared/services/indexed-db.service';
import { ValueChangedEvent } from 'devextreme/ui/check_box';
import { environment } from '../environments/environment';
import { version } from 'package.json';
import { confirm } from 'devextreme/ui/dialog';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnDestroy {
  isAuthenticated$: Observable<boolean>;
  loadingPage: boolean = false;
  deferredPrompt: any;
  isInstallPromptVisible: boolean = false;
  isInstallPromptVisibleIOS: boolean = false;
  beforeinstallprompt: boolean = false;
  authIsDone: boolean = false;
  isOffline: boolean = !navigator.onLine;
  versionMissmatch: boolean = false;
  feVersion: string = version;
  backendVersion: string = '';
  expectedVersion: string = environment.expectedBeVersion;

  private _navigatorLanguage: string = navigator.language || navigator.languages[0];

  private _unSubscriber: Subject<any> = new Subject();

  @HostBinding('class') get getClass() {
    return Object.keys(this.screen.sizes).filter(cl => this.screen.sizes[cl]).join(' ');
  }

  constructor(
    public readonly loadingService: LoadingService,
    public appInfo: AppInfoService,
    private screen: ScreenService,
    private translate: TranslateService,
    private _swUpdate: SwUpdate,
    private readonly _baseAuthSvc: AuthService,
    private readonly _authService: AuthUserService<User, MachineOwner>,
    private readonly _remoteDataWrapperService: RemoteDataWrapperService,
    private readonly _dashboardDataService: DashboardDataService,
    private readonly _indexedDBSvc: IndexedDBService,
    private readonly _versionService: VersionService,
    private readonly _translate: TranslateWithFallbackPipe
  ) {
    

    this.setLocalization(this._navigatorLanguage);
    this.isAuthenticated$ = this._authService.isAuthenticated$;

    // Check for updates PWA
    this._swUpdate.versionUpdates.subscribe(event => {
      if (event.type === 'VERSION_READY') {

        confirm(this._translate.transform('do-you-want-to-install'), this._translate.transform('new-version-available')).then(val => {
          if (val) {
            window.location.reload();
          }
        })
      }
    });

    // Check online and offline status
    window.addEventListener('online', () => {
      this.isOffline = false;
    });

    window.addEventListener('offline', () => {
      this.isOffline = true;
    });
  }

  ngOnInit(): void {
    // Check if user is authenticated
    this._authService.isAuthenticated$.pipe(takeUntil(this._unSubscriber)).subscribe((val) => {
      if (val) {
        this._authService.reloadLoggedUser();
        this._authService.reloadLoggedUserMachineOwner();
        // Potrebne pre setnutie current machine aj ked prva nacitana stranka nebude dashboard.
        this._dashboardDataService.initDashboard();
        this._dashboardDataService.listenOnCurrentMachineChange();
        this.authIsDone = true;
        this._showInstallPrompt();

        this._versionService.getCurrentVersionVersion().subscribe(version => {
          if (!version.startsWith(this.expectedVersion)) {
            this.versionMissmatch = true;
            this.backendVersion = version;
          }
        });
      }
    });

    this._authService.userDetail$.pipe(takeUntil(this._unSubscriber)).subscribe(val => {
      if (val?.PreferredLanguage && !this._navigatorLanguage.startsWith(val.PreferredLanguage)) {
        this.setLocalization(val.PreferredLanguage);
      }
    });

    window.addEventListener('beforeinstallprompt', (event) => {
      event.preventDefault();
      this.deferredPrompt = event;
      this.beforeinstallprompt = true;
      this._showInstallPrompt();
    });
  }

  ngOnDestroy(): void {
      this._unSubscriber.next(null);
      this._unSubscriber.complete();
      this._remoteDataWrapperService.hardDisconnectAll();
      window.removeEventListener('beforeinstallprompt', () => { });
      window.removeEventListener('online', () => { });
      window.removeEventListener('offline', () => { });
  }

  public dontShowInstallPrompt(result: ValueChangedEvent): void {
    this._indexedDBSvc.setSettings('dontShowInstallPrompt', result.value);
  }

  public isAuthenticated(): boolean {
    return this._baseAuthSvc.hasValidToken();
  }

  private _isIOS(): boolean {
    return /iPad|iPhone|iPod|Mac|OS X/.test(navigator.userAgent);
  }

  private _showInstallPrompt(): void {
    // Show install prompt for PWA
    if (this.beforeinstallprompt && this.authIsDone){
      this._indexedDBSvc.getSettings('dontShowInstallPrompt').then((dontShowInstallPrompt) => {
        if (!dontShowInstallPrompt) {
          if (this._isIOS()) {
            this._indexedDBSvc.getSettings('hasBeenShowedPrompt').then((hasBeenShowedPrompt) => {
              if (!hasBeenShowedPrompt) {
                this.isInstallPromptVisibleIOS = true;
              }
            }).catch(() => {
              this.isInstallPromptVisibleIOS = true;
            });
          } else {
            this.isInstallPromptVisible = true;
          }
        }
      });
    }
  }

  setLocalization(browserLang: string) {
    switch (browserLang) {
      case 'de':
      case 'de-AT':
      case 'de-CH':
      case 'de-DE':
        loadMessages(this.mergeMessages(deDevExpressMessages, deMessages));
        locale('de');
        this.translate.use('de');
        break;
      case 'it':
      case 'it-IT':
        loadMessages(this.mergeMessages(itDevExpressMessages, itMessages));
        locale('it');
        this.translate.use('it');
        break;
      case 'fr':
      case 'fr-CA':
      case 'fr-CH':
      case 'fr-FR':
        loadMessages(this.mergeMessages(frDevExpressMessages, frMessages));
        locale('fr');
        this.translate.use('fr');
        break;
      case 'tr':
      case 'tr-TR':
        loadMessages(this.mergeMessages(trDevExpressMessages, trMessages));
        locale('tr');
        this.translate.use('tr');
        break;
      case 'es':
      case 'es-ES':
        loadMessages(this.mergeMessages(esDevExpressMessages, esMessages));
        locale('es');
        this.translate.use('es');
        break;
      case 'cs':
      case 'cs-CS':
        loadMessages(this.mergeMessages(csDevExpressMessages, csMessages));
        locale('cs');
        this.translate.use('cs');
        break;
      case 'en':
      case 'en-US':
      case 'en-GB':
      default:
        loadMessages(this.mergeMessages(enDevExpressMessages, enMessages));
        locale('en');
        this.translate.use('en');
        break;
    }
  }

  mergeMessages(devextremeMessages: any, customMessages: any) {
    return { ...devextremeMessages, ...customMessages };
  };

  closeInstallPromptIOS() {
    this.isInstallPromptVisibleIOS = false;
    this._indexedDBSvc.setSettings('hasBeenShowedPrompt', true);
  }

  installApp() {
    if (!this.deferredPrompt) {
      return;
    }
    this.deferredPrompt.prompt();
    this.deferredPrompt.userChoice.then((choiceResult: any) => {
      if (choiceResult.outcome === 'accepted') {
        console.log('User accepted the install prompt');
      } else {
        console.log('User dismissed the install prompt');
      }
      this.deferredPrompt = null;
    });
  }

}
